import React from 'react';
import { Button } from '../ui/button';
import { ShoppingCart, ExternalLink, X } from 'lucide-react';

const ProductDetailModal = ({ product, isOpen, onClose, formatPrice, currency, setCurrency }) => {
 if (!isOpen || !product) return null;

 return (
 <div 
 className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" 
 onClick={onClose}
 >
 <div 
 className="bg-white dark:bg-slate-900 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" 
 onClick={(e) => e.stopPropagation()}
 >
 {/* Modal Header */}
 <div className="sticky top-0 bg-white dark:bg-slate-900 border-b p-4 flex items-center justify-between">
 <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{product.name}</h2>
 <button 
 onClick={onClose}
 className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
 >
 <X className="w-6 h-6" />
 </button>
 </div>

 {/* Modal Content */}
 <div className="p-6 space-y-6">
 {/* Product Image with Sale Banner */}
 <div className="relative rounded-lg overflow-hidden bg-gradient-to-br from-orange-200 to-yellow-200 aspect-video">
 {product.image ? (
 <img 
 src={product.image} 
 alt={product.name}
 className="w-full h-full object-cover"
 />
 ) : (
 <div className="w-full h-full flex items-center justify-center">
 <ShoppingCart className="w-24 h-24 text-white" />
 </div>
 )}
 
 {product.on_sale && (
 <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 font-bold text-lg shadow-lg rounded">
 🏷️ SALE
 {product.msrp && product.msrp > product.price && (
 <span className="block text-sm">
 {Math.round(((product.msrp - product.price) / product.msrp) * 100)}% OFF
 </span>
 )}
 </div>
 )}
 </div>

 {/* Product Info Grid */}
 <div className="grid md:grid-cols-2 gap-6">
 {/* Left Column */}
 <div className="space-y-4">
 {/* Company Logo */}
 {product.company_logo && (
 <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
 <p className="text-xs text-slate-600 dark:text-neutral-400 mb-2">Sold by:</p>
 <img 
 src={product.company_logo} 
 alt="Company" 
 className="h-8 object-contain"
 />
 </div>
 )}

 {/* Category & Machine */}
 <div className="flex gap-2">
 <span className="px-3 py-1 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 text-sm font-medium rounded">
 {product.category}
 </span>
 {product.machine_name && (
 <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-sm font-medium rounded">
 🔧 {product.machine_name}
 </span>
 )}
 </div>

 {/* Description */}
 {product.description && (
 <div>
 <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Description</h3>
 <p className="text-slate-600 dark:text-neutral-400 text-sm">{product.description}</p>
 </div>
 )}
 </div>

 {/* Right Column - Pricing */}
 <div className="space-y-4">
 <div className="p-4 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
 <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Pricing</h3>
 
 {product.msrp && product.msrp > product.price ? (
 <>
 <div className="mb-2">
 <p className="text-sm text-slate-500 dark:text-neutral-500">Original Price</p>
 <p className="text-lg line-through text-slate-400">
 {formatPrice(product.msrp)}
 </p>
 </div>
 <div className="mb-2">
 <p className="text-sm text-slate-500 dark:text-neutral-500">Sale Price</p>
 <p className="text-3xl font-bold text-red-600">
 {formatPrice(product.price)}
 </p>
 </div>
 <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded">
 <p className="text-sm font-semibold text-green-700 dark:text-green-400">
 💰 You Save: {formatPrice(product.msrp - product.price)}
 </p>
 </div>
 </>
 ) : (
 <p className="text-3xl font-bold text-orange-600">
 {formatPrice(product.price)}
 </p>
 )}

 {/* Currency Toggle in Modal */}
 <div className="mt-4 pt-4 border-t">
 <p className="text-xs text-slate-600 dark:text-neutral-400 mb-2">View in:</p>
 <div className="flex gap-2">
 <Button
 variant={currency === 'CAD' ? 'default' : 'outline'}
 size="sm"
 onClick={() => setCurrency('CAD')}
 >
 🇨🇦 CAD
 </Button>
 <Button
 variant={currency === 'USD' ? 'default' : 'outline'}
 size="sm"
 onClick={() => setCurrency('USD')}
 >
 🇺🇸 USD
 </Button>
 </div>
 </div>
 </div>

 {/* Buy Now Button */}
 {product.external_url ? (
 <Button
 className="w-full bg-orange-600 hover:bg-orange-700 text-lg py-6"
 onClick={() => window.open(product.external_url, '_blank')}
 >
 Buy Now <ExternalLink className="w-5 h-5 ml-2" />
 </Button>
 ) : (
 <Button className="w-full" disabled>
 Coming Soon
 </Button>
 )}

 {/* Third Party Notice */}
 <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-slate-600 dark:text-neutral-400">
 <p className="font-semibold mb-1">⚠️ Third-Party Product</p>
 <p>Clicking "Buy Now" opens the seller's website. Peak and Prairie Industries and Freeze Dry Academy are not responsible for this transaction.</p>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
};

export default ProductDetailModal;
